// frontend/src/pages/OrganismComparePage.tsx
// Compare two photos of one organism
// (/tanks/:tankId/livestock/:livestockId/compare?before=&after=; livestock
// compare design 8.2): the organism's photos in the shared compare page
// (features/livestock/ComparePageBody.tsx). The subject comes from the URL, so
// a link always shows that organism; its tank is selected once when another is
// (useTankFromPath, plan decision 10), and a later switch in the tank picker
// leaves for /livestock. Back goes back in history, as the organism page's own
// Back does (decision 17).

import { useMemo } from 'react';
import { Alert, Button, Container, Group, Text } from '@mantine/core';
import { IconAlertCircle, IconLayoutColumns } from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { hasStatus } from '@/api/client';
import { EmptyState } from '@/components/common/EmptyState';
import { useOrganism } from '@/hooks/useLivestockPhotos';
import { ComparePageBody } from '@/features/livestock/ComparePageBody';
import { parseShotId } from '@/features/livestock/compareShots';
import type { PhotoSubject } from '@/features/livestock/photoSubject';
import { useTankFromPath } from '@/features/livestock/useTankParam';

export function OrganismComparePage(): JSX.Element {
  const { tankId, livestockId } = useParams<{ tankId: string; livestockId: string }>();
  const id = parseShotId(livestockId ?? null);
  const switching = useTankFromPath(tankId);
  const organism = useOrganism(switching ? undefined : tankId, id ?? undefined);
  // One object per organism, so a re-render never hands the body a new subject
  const subject = useMemo<PhotoSubject | undefined>(
    () =>
      tankId && id !== null && !switching
        ? { kind: 'organism', tankId, livestockId: id }
        : undefined,
    [tankId, id, switching]
  );

  // 403 (another account's tank) or 404 (no such organism in the tank): not here.
  // Anything else (the server or the network failing) can be tried again.
  const notHere =
    !tankId || id === null || (organism.isError && hasStatus(organism.error, 403, 404));
  if (notHere) {
    return (
      <Container size="lg" py="xl">
        <EmptyState
          icon={IconLayoutColumns}
          title="This organism isn't here"
          description="It may have been deleted, or be in a tank that isn't in your account."
          primaryAction={{ label: 'Back to Livestock', to: '/livestock' }}
        />
      </Container>
    );
  }

  if (organism.isError) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="This organism didn't load">
          <Group gap="sm">
            <Text size="sm">{organism.error.message}</Text>
            <Button
              size="compact-sm"
              variant="light"
              color="red"
              onClick={() => void organism.refetch()}
            >
              Try again
            </Button>
          </Group>
        </Alert>
      </Container>
    );
  }

  const name = organism.data?.common_name ?? '';
  const shareOrganism = organism.data
    ? {
        commonName: organism.data.common_name,
        scientificName: organism.data.scientific_name,
        livestockType: organism.data.livestock_type,
      }
    : undefined;
  return (
    <ComparePageBody
      subject={subject}
      backMode="history"
      subtitle={name}
      backTo={`/tanks/${encodeURIComponent(tankId)}/livestock/${id}`}
      backLabel={name ? `Back to ${name}` : 'Back'}
      switching={switching || organism.isLoading}
      switchKey={`${tankId}/${id}`}
      emptyDescription="Take a photo from the same angle every few weeks to see it grow"
      shareName={name}
      shareOrganism={shareOrganism}
    />
  );
}
